import { Button, CheckboxFormField, Divider, Form, Icon, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import { ArrowDownIcon } from 'assets/icons';
import classNames from 'classnames';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import ConfirmationDialog from 'components/foundation/dialogs/ConfirmationDialog/ConfirmationDialog';
import BasicPickerFormField from 'components/foundation/form-fields/BasicPickerFormField/BasicPickerFormField';
import { IBasicPickerValue } from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import { usePhotoVerificationPpeEnabled } from 'containers/photo-verification-report/hooks/usePhotoVerificationPpeFeatureFlags';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { useToastDispatch } from 'contexts/ToastContext';
import { useOpenable, usePositionUpdate } from 'hooks';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useRef } from 'react';
import { ClassName } from 'types/ClassName';
import IPosition from 'types/Position';
import {
  convertPhotoVerificationSettingsToString,
  getPhotoVerificationSettings,
  IPhotoVerificationSettings,
  isPPEEnabledForAction,
  PositionPhotoVerificationPPESettingsScanType,
  PositionPhotoVerificationSettingsActionType,
} from 'utils/positionUtils';
import {
  getPhotoVerificationSettingsFormActionKey,
  getPhotoVerificationSettingsFormActionTitle,
  getPhotoVerificationSettingsFormScanKey,
  getPhotoVerificationSettingsFormScanTitle,
  PhotoVerificationOptionType,
  photoVerificationSettingsFormAllActions,
  photoVerificationSettingsFormdDfaultActions,
  photoVerificationSettingsFormScans,
  updatePPESettings,
  useIsPPEScanTypeEnabled,
} from '../util/photoVerificationSettingsFormUtils';
import './PhotoVerificationSettingsForm.scss';

export interface IPhotoVerificationSettingsFormProps {
  className?: ClassName;
  position: IPosition;
  onSubmit: (settings: IPhotoVerificationSettings) => void;
}

export interface IPhotoVerificationSettingsFormData {
  enabled: PhotoVerificationOptionType;
  clockIn: boolean;
  clockInPPE: boolean;
  clockOut: boolean;
  clockOutPPE: boolean;
  startBreak: boolean;
  startBreakPPE: boolean;
  endBreak: boolean;
  endBreakPPE: boolean;
  switch: boolean;
  switchPPE: boolean;
  faceCover: boolean;
  headCover: boolean;
  safetyVest: boolean;
  eyeProtection: boolean;
}

const PhotoVerificationSettingsForm = (props: IPhotoVerificationSettingsFormProps) => {
  const { className, position, onSubmit } = props;

  const basicPickerOptions: Array<IBasicPickerValue<PhotoVerificationOptionType>> = [
    { id: PhotoVerificationOptionType.KIOSK_ONLY, name: t('Kiosk Only') },
    { id: PhotoVerificationOptionType.POSITION, name: t('Position') },
  ];

  const initialFormData = useRef<IPhotoVerificationSettingsFormData>(getInitialData());

  function getInitialData() {
    const photoVerificationSettings = getPhotoVerificationSettings(position);
    const actions = photoVerificationSettings.actions ?? photoVerificationSettingsFormdDfaultActions;
    const ppeSettings = photoVerificationSettings.ppe ?? [];
    const ppeScans = ppeSettings.find((setting) => setting.enabled)?.scans ?? [];

    return {
      enabled: photoVerificationSettings.enabled
        ? PhotoVerificationOptionType.POSITION
        : PhotoVerificationOptionType.KIOSK_ONLY,
      clockIn: actions.includes(PositionPhotoVerificationSettingsActionType.CLOCK_IN),
      clockInPPE: isPPEEnabledForAction(ppeSettings, PositionPhotoVerificationSettingsActionType.CLOCK_IN),
      clockOut: actions.includes(PositionPhotoVerificationSettingsActionType.CLOCK_OUT),
      clockOutPPE: isPPEEnabledForAction(ppeSettings, PositionPhotoVerificationSettingsActionType.CLOCK_OUT),
      startBreak: actions.includes(PositionPhotoVerificationSettingsActionType.START_BREAK),
      startBreakPPE: isPPEEnabledForAction(ppeSettings, PositionPhotoVerificationSettingsActionType.START_BREAK),
      endBreak: actions.includes(PositionPhotoVerificationSettingsActionType.END_BREAK),
      endBreakPPE: isPPEEnabledForAction(ppeSettings, PositionPhotoVerificationSettingsActionType.END_BREAK),
      switch: actions.includes(PositionPhotoVerificationSettingsActionType.SWITCH),
      switchPPE: isPPEEnabledForAction(ppeSettings, PositionPhotoVerificationSettingsActionType.SWITCH),
      faceCover: ppeScans.includes(PositionPhotoVerificationPPESettingsScanType.FACE_COVER),
      headCover: ppeScans.includes(PositionPhotoVerificationPPESettingsScanType.HEAD_COVER),
      safetyVest: ppeScans.includes(PositionPhotoVerificationPPESettingsScanType.SAFETY_VEST),
      eyeProtection: ppeScans.includes(PositionPhotoVerificationPPESettingsScanType.EYE_PROTECTION),
    };
  }

  const [, update] = usePositionUpdate();
  const toastDispatch = useToastDispatch();
  const isPPEEnabled = usePhotoVerificationPpeEnabled();
  const isPPEScanTypeEnabled = useIsPPEScanTypeEnabled();
  const scansDialog = useOpenable();

  function renderFormFields(form: IFormRender<IPhotoVerificationSettingsFormData>) {
    return (
      <>
        <HeaderText title={t('Require Photo Verification')} className="my-4">
          <span>
            {t(
              `Select if photo verification should only be required when using a Kiosk that requires it, or if the position should always photo verify when performing certain clock actions (Position settings will override any Kiosk settings).`
            )}
          </span>
        </HeaderText>
        <BasicPickerFormField
          name="enabled"
          data={basicPickerOptions}
          form={form}
          closeButtonRender={() => (
            <span className="drop-icon">
              <Icon svg={ArrowDownIcon} />
            </span>
          )}
        />
        {form.state.data.enabled === PhotoVerificationOptionType.POSITION && (
          <>
            <DashboardRibbon theme={Theme.LIGHT} className="my-6">
              <div className="action-grid mx-4">
                <div className="row header">
                  <h4>{t('When To Scan')}</h4>
                  {isPPEEnabled ? <h4>{t('Enable PPE Scan')}</h4> : <div></div>}
                </div>

                {photoVerificationSettingsFormAllActions.map((action) => {
                  return (
                    <>
                      <Divider />
                      <Divider className="mb-3" />
                      <div key={getPhotoVerificationSettingsFormActionKey(action)} className="row">
                        <CheckboxFormField
                          name={getPhotoVerificationSettingsFormActionKey(action)}
                          form={form}
                          label={getPhotoVerificationSettingsFormActionTitle(action)}
                          onChange={(value) => {
                            switch (action) {
                              case PositionPhotoVerificationSettingsActionType.CLOCK_IN:
                                form.state.data.clockIn = value;
                                break;
                              case PositionPhotoVerificationSettingsActionType.CLOCK_OUT:
                                form.state.data.clockOut = value;
                                break;
                              case PositionPhotoVerificationSettingsActionType.START_BREAK:
                                form.state.data.startBreak = value;
                                break;
                              case PositionPhotoVerificationSettingsActionType.END_BREAK:
                                form.state.data.endBreak = value;
                                break;
                              case PositionPhotoVerificationSettingsActionType.SWITCH:
                                form.state.data.switch = value;
                                break;
                            }
                          }}
                        />
                        {isPPEEnabled ? (
                          <CheckboxFormField
                            name={getPhotoVerificationSettingsFormActionKey(action) + 'PPE'}
                            form={form}
                            disabled={!getActionValue(action, form.state.data)}
                            onChange={(value) => {
                              switch (action) {
                                case PositionPhotoVerificationSettingsActionType.CLOCK_IN:
                                  form.state.data.clockInPPE = value;
                                  break;
                                case PositionPhotoVerificationSettingsActionType.CLOCK_OUT:
                                  form.state.data.clockOutPPE = value;
                                  break;
                                case PositionPhotoVerificationSettingsActionType.START_BREAK:
                                  form.state.data.startBreakPPE = value;
                                  break;
                                case PositionPhotoVerificationSettingsActionType.END_BREAK:
                                  form.state.data.endBreakPPE = value;
                                  break;
                                case PositionPhotoVerificationSettingsActionType.SWITCH:
                                  form.state.data.switchPPE = value;
                                  break;
                              }
                            }}
                          />
                        ) : (
                          <div></div>
                        )}
                      </div>
                    </>
                  );
                })}
              </div>
              {isPPEScansVisible(form.state.data) && (
                <div className="mx-4 mt-6">
                  <HeaderText title={t('PPE Scan Items')}>
                    <span>{t(`Select the PPE items you wish to scan for.`)}</span>
                  </HeaderText>

                  <div className="mt-2 ppe-scan-container">
                    {photoVerificationSettingsFormScans
                      .filter((scan) => isPPEScanTypeEnabled(scan))
                      .map((scan) => {
                        return (
                          <CheckboxFormField
                            key={getPhotoVerificationSettingsFormScanKey(scan)}
                            name={getPhotoVerificationSettingsFormScanKey(scan)}
                            form={form}
                            label={getPhotoVerificationSettingsFormScanTitle(scan)}
                            onChange={(value) => {
                              switch (scan) {
                                case PositionPhotoVerificationPPESettingsScanType.FACE_COVER:
                                  form.state.data.faceCover = value;
                                  break;
                                case PositionPhotoVerificationPPESettingsScanType.HEAD_COVER:
                                  form.state.data.headCover = value;
                                  break;
                                case PositionPhotoVerificationPPESettingsScanType.SAFETY_VEST:
                                  form.state.data.safetyVest = value;
                                  break;
                                case PositionPhotoVerificationPPESettingsScanType.EYE_PROTECTION:
                                  form.state.data.eyeProtection = value;
                                  break;
                              }
                            }}
                          />
                        );
                      })}
                  </div>
                </div>
              )}
            </DashboardRibbon>
          </>
        )}

        <Button type="primary" onClick={form.handleSubmit} className="mt-4">
          {t('Update')}
        </Button>
      </>
    );
  }

  function getActionValue(
    action: PositionPhotoVerificationSettingsActionType,
    data: IPhotoVerificationSettingsFormData
  ) {
    switch (action) {
      case PositionPhotoVerificationSettingsActionType.CLOCK_IN:
        return data.clockIn;
      case PositionPhotoVerificationSettingsActionType.CLOCK_OUT:
        return data.clockOut;
      case PositionPhotoVerificationSettingsActionType.START_BREAK:
        return data.startBreak;
      case PositionPhotoVerificationSettingsActionType.END_BREAK:
        return data.endBreak;
      case PositionPhotoVerificationSettingsActionType.SWITCH:
        return data.switch;
    }
  }

  function isPPEScansVisible(data: IPhotoVerificationSettingsFormData) {
    return (
      isPPEEnabled && (data.clockInPPE || data.clockOutPPE || data.startBreakPPE || data.endBreakPPE || data.switchPPE)
    );
  }

  function noScansSelected(data: IPhotoVerificationSettingsFormData) {
    return !data.faceCover && !data.headCover && !data.safetyVest && !data.eyeProtection;
  }

  async function onFormSubmit(data: IPhotoVerificationSettingsFormData) {
    if (isPPEScansVisible(data) && noScansSelected(data)) {
      scansDialog.open();
      return;
    }

    let newActions = [];
    if (data.clockIn) {
      newActions.push(PositionPhotoVerificationSettingsActionType.CLOCK_IN);
    }

    if (data.clockOut) {
      newActions.push(PositionPhotoVerificationSettingsActionType.CLOCK_OUT);
    }
    if (data.startBreak) {
      newActions.push(PositionPhotoVerificationSettingsActionType.START_BREAK);
    }

    if (data.endBreak) {
      newActions.push(PositionPhotoVerificationSettingsActionType.END_BREAK);
    }

    if (data.switch) {
      newActions.push(PositionPhotoVerificationSettingsActionType.SWITCH);
    }

    if (isEmpty(newActions) && data.enabled === PhotoVerificationOptionType.POSITION) {
      toastDispatch({
        type: 'OPEN',
        payload: {
          theme: Theme.DANGER,
          label: t('At least one employee clock action must be enabled.'),
        },
      });
      return;
    } else if (isEmpty(newActions) && data.enabled !== PhotoVerificationOptionType.POSITION) {
      newActions = getPhotoVerificationSettings(position).actions ?? photoVerificationSettingsFormdDfaultActions;
    }

    const ppeSettings = updatePPESettings(
      position,
      isPPEEnabled,
      {
        clockInPPE: data.clockInPPE,
        clockOutPPE: data.clockOutPPE,
        startBreakPPE: data.startBreakPPE,
        endBreakPPE: data.endBreakPPE,
        switchPPE: data.switchPPE,
      },
      {
        faceCover: data.faceCover,
        headCover: data.headCover,
        safetyVest: data.safetyVest,
        eyeProtection: data.eyeProtection,
      }
    );

    const settings: IPhotoVerificationSettings = {
      enabled: data.enabled === PhotoVerificationOptionType.POSITION,
      actions: newActions,
      ppe: ppeSettings,
    };
    try {
      await update({
        id: position.id,
        photoVerificationSettings: convertPhotoVerificationSettingsToString(settings),
      });
    } catch (e) {
      toastDispatch({
        type: 'OPEN',
        payload: {
          theme: Theme.DANGER,
          label: t('There was a problem updating your position. Please try again later.'),
        },
      });
    }
    onSubmit(settings);
  }

  const classes = classNames('photo-verification-settings-form', className);

  return (
    <>
      <Form
        className={classes}
        render={renderFormFields}
        onSubmit={onFormSubmit}
        data={initialFormData.current}
        allowMultipleSubmissions={true}
      />
      <ConfirmationDialog
        isOpen={scansDialog.isOpen}
        title={t('No Scan Items Selected')}
        message={t('In order to have a PPE Scan enabled, you must select at least one PPE Scan Item.')}
        closeButtonText={t('Close')}
        onClose={scansDialog.close}
        className={classes}
      />
    </>
  );
};

export default PhotoVerificationSettingsForm;
